import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter } from 'lucide-react';
import { Language } from '../App';
interface FooterProps {
  language: Language;
}
const logos = {
  english: 'https://cdn.magicpatterns.com/uploads/2WciLPDBTDd6muQs1migkU/English_Logo.jpg',
  pashto: 'https://cdn.magicpatterns.com/uploads/7CKdKs1tCA5hzvjCodsXWM/Pahto_Logo.jpg'
};
export function Footer({
  language
}: FooterProps) {
  const isPashto = language === 'pashto';
  const quickLinks = [{
    en: 'Home',
    ps: 'کور',
    path: '/'
  }, {
    en: 'Magazine',
    ps: 'مجله',
    path: '/magazine'
  }, {
    en: 'About Us',
    ps: 'زموږ په اړه',
    path: '/about'
  }, {
    en: 'Support',
    ps: 'ملاتړ',
    path: '/subscription'
  }, {
    en: 'Archive',
    ps: 'آرشیف',
    path: '/archive'
  }, {
    en: 'Contact Us',
    ps: 'اړیکه ونیسئ',
    path: '/contact'
  }];
  const policyLinks = [{
    en: 'Editorial Policy',
    ps: 'تحریري پالیسي',
    path: '/editorial-policy'
  }, {
    en: 'Privacy & Cookie Policy',
    ps: 'محرمیت او کوکي پالیسي',
    path: '/privacy-policy'
  }, {
    en: 'Terms & Conditions',
    ps: 'شرایط او ضوابط',
    path: '/terms'
  }];
  return <footer className="relative" data-id="element-94">
      {/* Made by Bright Pixxel - light strip above gradient footer */}
      <div className="bg-white border-t border-gray-100" data-id="element-95">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-center" data-id="element-96">
          <a href="https://bright-pixxel.com/" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-3 text-gray-600 hover:text-gray-900 transition-colors" aria-label="Made by Bright Pixxel Studio" data-id="element-97">
            <span className={`text-sm font-medium ${isPashto ? 'font-pashto' : ''}`} data-id="element-98">
              {isPashto ? 'جوړونکی' : 'Made by'}
            </span>
            <img src="https://cdn.magicpatterns.com/uploads/1ibFbDjREg9Ru2yCYj8ErJ/Transparent_-_Logo_BrightPixel.png" alt="Bright Pixxel Studio" className="h-9 md:h-10 w-auto transition-transform group-hover:scale-105" data-id="element-99" />
          </a>
        </div>
      </div>

      {/* Gradient footer wrapper */}
      <div className="relative overflow-hidden" data-id="element-100">
        {/* Gradient background matching header */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#C2185B] to-[#7B1FA2]" data-id="element-101">
          {/* Subtle bird pattern */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMaxYMid meet" viewBox="0 0 1200 400" data-id="element-102">
            <g fill="white" opacity="0.04" data-id="element-103">
              <path d="M820,145 C810,130 795,120 780,115 C790,125 795,135 800,142 C790,138 775,132 760,130 C778,138 790,142 800,145 C795,148 790,155 785,165 C795,155 800,148 805,145 C810,148 815,155 820,165 C815,155 812,148 810,145 C820,142 835,138 850,130 C835,132 820,138 810,142 C815,135 820,125 830,115 Z" transform="translate(200,50) scale(0.7)" data-id="element-104" />
              <path d="M920,235 C912,222 900,215 888,212 C896,220 900,228 903,233 C895,230 884,225 872,224 C886,230 895,233 903,235 C900,238 896,244 892,252 C898,244 902,238 905,235 C908,238 912,244 916,252 C912,244 910,238 908,235 C916,233 926,230 938,224 C926,225 916,230 908,233 C912,228 916,220 924,212 Z" transform="translate(-100,80) scale(0.5)" data-id="element-105" />
            </g>
          </svg>
        </div>

        <div className="relative z-10" data-id="element-106">
          {/* Main Footer Content */}
          <div className="max-w-7xl mx-auto px-6 py-10 md:py-12" data-id="element-107">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6" data-id="element-108">
              {/* Column 1: Logo & Description */}
              <div className="lg:col-span-1" data-id="element-109">
                <div className="flex items-center gap-3 mb-4" data-id="element-110">
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/20 flex-shrink-0" data-id="element-111">
                    <img src="https://cdn.magicpatterns.com/uploads/a1wFSzqfrcuN2xyXncDR6P/Pasoon.png" alt="Pasoon Logo" className="w-full h-full object-contain" data-id="element-112" />
                  </div>
                  <span className="text-white font-serif text-2xl font-bold" data-id="element-113">
                    Pasoon
                  </span>
                </div>
                <p className={`text-white/70 text-sm leading-relaxed ${isPashto ? 'font-pashto leading-loose' : ''}`} data-id="element-114">
                  {isPashto ? 'پاڅون خپرندویه ادارې د پښتو لیکوالانو، شاعرانو او ژبپوهانو آثار د کتابونو، مجلو او خپرونو په بڼه تولیدوي.' : 'Pasoon Publishers produces the work of Pashto writers, poets, and linguists through books, magazines, and publications.'}
                </p>
              </div>

              {/* Column 2: Quick Links + Policies */}
              <div data-id="element-115">
                <h4 className={`text-white font-bold text-base mb-3 ${isPashto ? 'font-pashto' : ''}`} data-id="element-116">
                  {isPashto ? 'چټکې لینکونه' : 'Quick Links'}
                </h4>
                <ul className="space-y-1.5" data-id="element-117">
                  {quickLinks.map((link, idx) => <li key={idx} data-id="element-118">
                      <Link to={link.path} className={`text-white/70 hover:text-white text-sm transition-colors ${isPashto ? 'font-pashto' : ''}`} data-id="element-119">
                        {isPashto ? link.ps : link.en}
                      </Link>
                    </li>)}
                </ul>

                {/* Divider */}
                <div className="w-full h-px bg-white/10 my-3" data-id="element-120"></div>

                {/* Policy Links */}
                <ul className="space-y-1.5" data-id="element-121">
                  {policyLinks.map((link, idx) => <li key={idx} data-id="element-122">
                      <Link to={link.path} className={`text-white/50 hover:text-white/80 text-xs transition-colors ${isPashto ? 'font-pashto' : ''}`} data-id="element-123">
                        {isPashto ? link.ps : link.en}
                      </Link>
                    </li>)}
                </ul>
              </div>

              {/* Column 3: Contact Details */}
              <div data-id="element-124">
                <h4 className={`text-white font-bold text-base mb-3 ${isPashto ? 'font-pashto' : ''}`} data-id="element-125">
                  {isPashto ? 'د اړیکې معلومات' : 'Contact Details'}
                </h4>
                <ul className="space-y-2.5 text-sm" data-id="element-126">
                  <li data-id="element-127">
                    <span className={`text-white/50 block text-xs mb-0.5 ${isPashto ? 'font-pashto' : ''}`} data-id="element-128">
                      {isPashto ? 'تلیفون' : 'Phone'}
                    </span>
                    <span className="text-white/80" dir="ltr" data-id="element-129">
                      0937 - 874489
                    </span>
                  </li>
                  <li data-id="element-130">
                    <span className={`text-white/50 block text-xs mb-0.5 ${isPashto ? 'font-pashto' : ''}`} data-id="element-131">
                      {isPashto ? 'بریښنالیک' : 'Email'}
                    </span>
                    <a href="mailto:pasoonmardan@gmail.com" className="text-white/80 hover:text-white transition-colors" data-id="element-132">
                      pasoonmardan@gmail.com
                    </a>
                  </li>
                  <li data-id="element-133">
                    <span className={`text-white/50 block text-xs mb-0.5 ${isPashto ? 'font-pashto' : ''}`} data-id="element-134">
                      {isPashto ? 'ویبپاڼه' : 'Website'}
                    </span>
                    <a href="https://www.pasoon.org" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors" data-id="element-135">
                      www.pasoon.org
                    </a>
                  </li>
                </ul>
              </div>

              {/* Column 4: Social Links */}
              <div data-id="element-136">
                <h4 className={`text-white font-bold text-base mb-3 ${isPashto ? 'font-pashto' : ''}`} data-id="element-137">
                  {isPashto ? 'ټولنیزې شبکې' : 'Follow Us'}
                </h4>
                <div className="flex gap-3" data-id="element-138">
                  <a href="https://www.facebook.com/profile.php?id=100063981965920&locale=en_GB" target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" aria-label="Facebook" data-id="element-139">
                    <Facebook className="w-5 h-5" data-id="element-140" />
                  </a>
                  <a href="https://x.com/pasoonmardan?s=20" target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" aria-label="X (Twitter)" data-id="element-141">
                    <Twitter className="w-5 h-5" data-id="element-142" />
                  </a>
                </div>

                <p className={`text-white/40 text-xs mt-5 leading-relaxed ${isPashto ? 'font-pashto leading-loose' : ''}`} data-id="element-143">
                  {isPashto ? 'پاڅون خپرندویه ادارې لا دمخه کتابونه خپاره کړي دي. پکې ملي اتل، ملي پاڅون شامل دي او نور یو څو د خپرولو په بهیر کې دي.' : 'Pasoon Publishers has already published books including Milli Atal, Milli Pasoon, and a few more are in the process of publishing.'}
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10" data-id="element-144">
            <div className="max-w-7xl mx-auto px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-145">
              <p className={`text-white/50 text-xs ${isPashto ? 'font-pashto' : ''}`} data-id="element-146">
                {isPashto ? '© پاڅون مجله. ټول حقونه خوندي دي.' : '© Pasoon Magazine. All rights reserved.'}
              </p>
              <p className={`text-white/30 text-xs ${isPashto ? 'font-pashto' : ''}`} data-id="element-147">
                {isPashto ? 'مردان، خیبر پښتونخوا، پاکستان' : 'Mardan, Khyber Pakhtunkhwa, Pakistan'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>;
}